import React from 'react';
import { useSelector } from 'react-redux';
import NavLink from '../NavLink/navlink-component';

// ─── Schema builder ────────────────────────────────────────────────────────────
export function buildBreadcrumbSchema(crumbs, siteUrl) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: crumbs.map((crumb, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: crumb.label,
            item: `${siteUrl}${crumb.href}`,
        })),
    };
}

// ─── Icons ────────────────────────────────────────────────────────────────────
function HomeIcon() {
    return (
        <svg className="w-3.5 h-3.5 lg:w-4 lg:h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
    );
}

function ChevronIcon({ isRtl }) {
    return (
        <svg
            className={`w-3 h-3 lg:w-3.5 lg:h-3.5 shrink-0 text-primary-300 ${isRtl ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        >
            <path d="M9 18l6-6-6-6" />
        </svg>
    );
}

// ─── Visible breadcrumb nav ────────────────────────────────────────────────────
function Breadcrumb({ crumbs, className = '' }) {
    const { dir = 'rtl' } = useSelector(s => s.systemData || {});
    const isRtl = dir === 'rtl';

    if (!crumbs?.length) return null;

    return (
        <nav
            aria-label="breadcrumb"
            dir={dir}
            className={`w-full bg-white border-b border-primary-100 px-6 lg:px-10 py-3 lg:py-4 ${className}`}
        >
            <ol className="max-w-[1400px] mx-auto flex items-center gap-1 flex-wrap">
                {crumbs.map((crumb, i) => {
                    const isFirst = i === 0;
                    const isLast  = i === crumbs.length - 1;
                    return (
                        <li key={i} className="flex items-center gap-1">
                            {i > 0 && <ChevronIcon isRtl={isRtl} />}
                            {isLast ? (
                                <span
                                    aria-current="page"
                                    className="text-[11px] lg:text-sm font-bold text-secondary-500 truncate max-w-[220px] lg:max-w-[360px] tracking-wide"
                                >
                                    {crumb.label}
                                </span>
                            ) : (
                                <NavLink
                                    href={crumb.href}
                                    preserveStyle
                                    className="inline-flex items-center gap-1 text-[11px] lg:text-sm font-bold text-primary-400 hover:text-primary-900 transition-colors cursor-pointer"
                                >
                                    {isFirst && <HomeIcon />}
                                    {crumb.label}
                                </NavLink>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}

export default Breadcrumb;
